import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  Mail, 
  Clock, 
  Calendar, 
  Users, 
  Bell, 
  CheckCircle,
  Zap,
  Headphones,
  Globe,
  Star,
  Shield,
  Cloud,
  Code,
  Palette,
  Database,
  Smartphone,
  ShoppingCart,
  ArrowRight,
  ArrowLeft,
  Settings,
  DollarSign,
  Download,
  Send,
  MessageCircle
} from 'lucide-react';
import { pagesAPI, newsletterAPI } from '../services/api';

const ComingSoon = () => {
  const [pageData, setPageData] = useState({
    title: 'Something Amazing is Coming',
    subtitle: 'We are working hard to bring you something incredible. Stay tuned!',
    description: 'Our team is putting the finishing touches on something special. Be the first to know when we launch.',
    content: {
      countdown: {
        days_label: 'Days',
        hours_label: 'Hours',
        minutes_label: 'Minutes',
        seconds_label: 'Seconds',
        launch_date: '2024-12-31T23:59:59'
      },
      newsletter: {
        title: 'Get Notified',
        description: 'Be the first to know when we launch!',
        button_text: 'Notify Me',
        placeholder: 'Enter your email address'
      },
      features: {
        title: 'What to Expect',
        items: [
          {
            icon: 'Users',
            title: 'Innovative Design',
            description: 'Cutting-edge user experience'
          },
          {
            icon: 'Zap',
            title: 'Advanced Features',
            description: 'Powerful functionality'
          },
          {
            icon: 'Headphones',
            title: '24/7 Support',
            description: 'Always here to help'
          }
        ]
      },
      social_links: {
        title: 'Follow Us',
        items: [
          {
            name: 'Twitter',
            url: '#',
            icon: 'twitter'
          },
          {
            name: 'Facebook',
            url: '#',
            icon: 'facebook'
          },
          {
            name: 'Instagram',
            url: '#',
            icon: 'instagram'
          }
        ]
      }
    }
  });

  const [timeLeft, setTimeLeft] = useState({
    days: 0,
    hours: 0,
    minutes: 0,
    seconds: 0
  });

  const [email, setEmail] = useState('');
  const [isSubscribed, setIsSubscribed] = useState(false);

  useEffect(() => {
    // Fetch page data from API
    const fetchPageData = async () => {
      try {
        const response = await pagesAPI.getByType('coming-soon');
        if (response?.data?.success) {
          const apiData = response.data.data;
          
          setPageData({
            title: apiData.title || pageData.title,
            subtitle: apiData.subtitle || pageData.subtitle,
            description: apiData.description || pageData.description,
            content: {
              countdown: apiData.content?.countdown || pageData.content.countdown,
              newsletter: apiData.content?.newsletter || pageData.content.newsletter,
              features: apiData.content?.features || pageData.content.features,
              social_links: apiData.content?.social_links || pageData.content.social_links,
              main_icon: apiData.content?.main_icon || 'Bell' // Add main_icon to state
            }
          });
        }
      } catch (error) {
        console.error('Error fetching page data:', error);
      }
    };

    fetchPageData();
  }, []);

  useEffect(() => {
    const calculateTimeLeft = () => {
      const difference = +new Date(pageData.content.countdown.launch_date) - +new Date();
      
      if (difference > 0) {
        setTimeLeft({
          days: Math.floor(difference / (1000 * 60 * 60 * 24)),
          hours: Math.floor((difference / (1000 * 60 * 60)) % 24),
          minutes: Math.floor((difference / 1000 / 60) % 60),
          seconds: Math.floor((difference / 1000) % 60)
        });
      }
    };

    calculateTimeLeft();
    const timer = setInterval(calculateTimeLeft, 1000);

    return () => clearInterval(timer);
  }, [pageData.content.countdown.launch_date]);

  const handleSubscribe = async (e) => {
    e.preventDefault();
    if (email.trim()) {
      try {
        const response = await newsletterAPI.subscribe({ email });
        if (response.data.success) {
          setIsSubscribed(true);
          setEmail('');
        }
      } catch (error) {
        console.error('Error subscribing:', error);
      }
    }
  };

  const CountdownBox = ({ value, label }) => (
    <motion.div
      whileHover={{ scale: 1.05 }}
      className="bg-white dark:bg-gray-800 rounded-lg p-6 text-center shadow-lg border border-gray-200 dark:border-gray-700"
    >
      <div className="text-3xl md:text-4xl font-bold text-blue-600 dark:text-blue-400">
        {value.toString().padStart(2, '0')}
      </div>
      <div className="text-sm text-gray-600 dark:text-gray-400 uppercase tracking-wide">
        {label}
      </div>
    </motion.div>
  );

  // Available icons for dynamic resolution
  const availableIcons = {
    'Users': Users,
    'Zap': Zap,
    'Headphones': Headphones,
    'Globe': Globe,
    'Mail': Mail,
    'Bell': Bell,
    'Star': Star,
    'Shield': Shield,
    'Cloud': Cloud,
    'Code': Code,
    'Palette': Palette,
    'Database': Database,
    'Smartphone': Smartphone,
    'ShoppingCart': ShoppingCart,
    'ArrowRight': ArrowRight,
    'ArrowLeft': ArrowLeft,
    'CheckCircle': CheckCircle,
    'Settings': Settings,
    'DollarSign': DollarSign,
    'Download': Download,
    'Send': Send,
    'MessageCircle': MessageCircle,
    'Clock': Clock,
    'Calendar': Calendar
  };

  const getIconComponent = (iconName) => {
    return availableIcons[iconName] || Users;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900">
      
      <main className="pt-16 lg:pt-20">
        {/* Animated Background */}
        <div className="absolute inset-0 pt-20">
          <div className="absolute inset-0 bg-gradient-to-br from-blue-600/10 to-purple-600/10 dark:from-purple-600/20 dark:to-blue-600/20"></div>
          <div className="absolute top-0 left-0 w-full h-full">
            {[...Array(30)].map((_, i) => (
              <motion.div
                key={i}
                className="absolute w-1 h-1 bg-blue-400/30 dark:bg-white/20 rounded-full"
                style={{
                  left: `${Math.random() * 100}%`,
                  top: `${Math.random() * 100}%`,
                }}
                animate={{
                  y: [0, -200, 0],
                  opacity: [0, 1, 0],
                }}
                transition={{
                  duration: 4 + Math.random() * 3,
                  repeat: Infinity,
                  delay: Math.random() * 3,
                }}
              />
            ))}
          </div>
        </div>

        <div className="relative z-10 flex flex-col items-center justify-center min-h-screen px-4 py-12">
          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: -50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1 }}
            className="text-center mb-12"
          >
            <motion.div
              animate={{ rotate: [0, 10, -10, 0] }}
              transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
              className="inline-block mb-8"
            >
              {(() => {
                const mainIcon = pageData.content?.main_icon || 'Bell';
                console.log('🌐 Main icon from pageData:', mainIcon);
                const MainIcon = getIconComponent(mainIcon);
                console.log('🎯 MainIcon component:', MainIcon);
                return <MainIcon className="w-20 h-20 text-purple-600 dark:text-purple-300 mx-auto" />;
              })()}
            </motion.div>
            
            <h1 className="text-5xl md:text-6xl font-bold text-gray-900 dark:text-white mb-4">
              {pageData.title}
            </h1>
            
            <p className="text-xl md:text-2xl text-purple-700 dark:text-purple-200 mb-6">
              {pageData.subtitle}
            </p>
            
            <p className="text-lg text-gray-600 dark:text-gray-200 max-w-2xl mx-auto">
              {pageData.description}
            </p>
          </motion.div>

          {/* Countdown Timer */}
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.3, duration: 0.8 }}
            className="mb-12"
          >
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 max-w-2xl mx-auto">
              <CountdownBox value={timeLeft.days} label={pageData.content.countdown.days_label} />
              <CountdownBox value={timeLeft.hours} label={pageData.content.countdown.hours_label} />
              <CountdownBox value={timeLeft.minutes} label={pageData.content.countdown.minutes_label} />
              <CountdownBox value={timeLeft.seconds} label={pageData.content.countdown.seconds_label} />
            </div>
          </motion.div>

          {/* Email Subscription */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6, duration: 0.8 }}
            className="mb-12"
          >
            <div className="bg-white/80 dark:bg-white/10 backdrop-blur-sm rounded-lg p-8 border border-white/50 dark:border-white/20 max-w-md mx-auto shadow-lg">
              <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
                {pageData.content.newsletter.title}
              </h3>
              <p className="text-gray-600 dark:text-gray-200 mb-6">
                {pageData.content.newsletter.description}
              </p>
              
              {!isSubscribed ? (
                <form onSubmit={handleSubscribe} className="space-y-4">
                  <div className="flex gap-2">
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder={pageData.content.newsletter.placeholder}
                      className="flex-1 px-4 py-3 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500 dark:focus:ring-purple-400"
                      required
                    />
                    <button
                      type="submit"
                      className="px-6 py-3 bg-purple-600 hover:bg-purple-700 dark:bg-purple-500 dark:hover:bg-purple-600 text-white font-semibold rounded-lg transition-colors"
                    >
                      {pageData.content.newsletter.button_text}
                    </button>
                  </div>
                </form>
              ) : (
                <div className="text-center">
                  <CheckCircle className="w-12 h-12 text-green-500 dark:text-green-400 mx-auto mb-4" />
                  <p className="text-green-600 dark:text-green-400 font-semibold">You're subscribed!</p>
                  <p className="text-gray-600 dark:text-gray-200 text-sm">We'll notify you when we launch.</p>
                </div>
              )}
            </div>
          </motion.div>

          {/* Features */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.9, duration: 0.8 }}
            className="mb-12"
          >
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6 text-center">
              {pageData.content.features.title}
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-4xl mx-auto">
              {pageData.content.features.items.map((feature, index) => {
                const IconComponent = getIconComponent(feature.icon);
                return (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 1.2 + index * 0.1, duration: 0.8 }}
                    whileHover={{ scale: 1.05 }}
                    className="bg-white/80 dark:bg-white/10 backdrop-blur-sm rounded-lg p-6 border border-white/50 dark:border-white/20 text-center shadow-lg"
                  >
                    <div className="w-12 h-12 bg-purple-600 dark:bg-purple-500 rounded-lg flex items-center justify-center mx-auto mb-4">
                      <IconComponent className="w-6 h-6 text-white" />
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">{feature.title}</h3>
                    <p className="text-gray-600 dark:text-gray-200">{feature.description}</p>
                  </motion.div>
                );
              })}
            </div>
          </motion.div>

          {/* Social Links */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.5, duration: 0.8 }}
          >
            <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-6 text-center">
              {pageData.content.social_links.title}
            </h3>
            <div className="flex justify-center gap-4">
              {pageData.content.social_links.items.map((social, index) => (
                <motion.a
                  key={index}
                  href={social.url}
                  initial={{ opacity: 0, scale: 0 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 1.8 + index * 0.1, duration: 0.8 }}
                  whileHover={{ scale: 1.1 }}
                  className="w-12 h-12 bg-white/80 dark:bg-white/10 backdrop-blur-sm rounded-lg flex items-center justify-center border border-white/50 dark:border-white/20 hover:bg-white dark:hover:bg-white/20 transition-colors shadow-lg"
                  title={social.name} // Add tooltip for accessibility
                >
                  {/* Use the icon selected in admin panel */}
                  {(() => {
                    const SocialIcon = getIconComponent(social.icon);
                    return <SocialIcon className="w-6 h-6 text-gray-700 dark:text-white" />;
                  })()}
                </motion.a>
              ))}
            </div>
          </motion.div>
        </div>
      </main>
      
    </div>
  );
};

export default ComingSoon; 